import type { TestListingCardVariant } from './types';
import type { ApiSubscriptionFlags } from './subscription';
import { isIconicTest, isPlusTest } from './subscription';

export type TestListingLockType = 'plus' | 'prime';

export interface TestListingLockContext {
  isPlusOrIconicUser: boolean;
  isFreeUserOnGoal: boolean;
  isOfflineCentreLearner: boolean;
  hasActiveTestOrNotesStoreSubscription: boolean;
  isPrimeEnabled: boolean;
  isPrimeQuotaExhausted: boolean;
}

export interface TestCardLockParams extends TestListingLockContext {
  subscriptionFlags: ApiSubscriptionFlags;
  isSellable?: boolean;
  variant?: TestListingCardVariant;
}

export interface TestListingLockInfo {
  showLock: boolean;
  lockType: TestListingLockType | null;
  isLockedPlus: boolean;
  unlockedBySellableStore: boolean;
}

/** Plus or Iconic test that requires a paid goal subscription */
export const shouldShowPurchaseForTest = (
  flags: ApiSubscriptionFlags
): boolean => isPlusTest(flags) || isIconicTest(flags);

export const isUnlockedViaSellableStoreSubscription = ({
  isSellable,
  hasActiveTestOrNotesStoreSubscription
}: Pick<
  TestCardLockParams,
  'isSellable' | 'hasActiveTestOrNotesStoreSubscription'
>): boolean => Boolean(isSellable) && hasActiveTestOrNotesStoreSubscription;

export const getAllowSellableStoreUnlock = (
  variant?: TestListingCardVariant
): boolean => variant === 'test_series';

export const getIsLockedPlus = ({
  subscriptionFlags,
  isFreeUserOnGoal,
  isOfflineCentreLearner,
  isSellable,
  hasActiveTestOrNotesStoreSubscription,
  variant
}: Pick<
  TestCardLockParams,
  | 'subscriptionFlags'
  | 'isFreeUserOnGoal'
  | 'isOfflineCentreLearner'
  | 'isSellable'
  | 'hasActiveTestOrNotesStoreSubscription'
  | 'variant'
>): boolean => {
  if (!isFreeUserOnGoal || isOfflineCentreLearner) {
    return false;
  }

  const shouldPurchase = shouldShowPurchaseForTest(subscriptionFlags);
  const unlockedBySellableStore =
    getAllowSellableStoreUnlock(variant) &&
    isUnlockedViaSellableStoreSubscription({
      isSellable,
      hasActiveTestOrNotesStoreSubscription
    });

  return shouldPurchase && !unlockedBySellableStore;
};

export const getTestListingLockInfo = (
  params: TestCardLockParams
): TestListingLockInfo => {
  const unlockedBySellableStore =
    getAllowSellableStoreUnlock(params.variant) &&
    isUnlockedViaSellableStoreSubscription({
      isSellable: params.isSellable,
      hasActiveTestOrNotesStoreSubscription:
        params.hasActiveTestOrNotesStoreSubscription
    });

  if (params.isPlusOrIconicUser || unlockedBySellableStore) {
    return {
      showLock: false,
      lockType: null,
      isLockedPlus: false,
      unlockedBySellableStore
    };
  }

  const isLockedPlus = getIsLockedPlus(params);
  const effectiveQuotaExhausted =
    params.isPrimeEnabled && params.isPrimeQuotaExhausted;
  const showLock = isLockedPlus || effectiveQuotaExhausted;

  let lockType: TestListingLockType | null = null;

  if (isLockedPlus) {
    lockType = 'plus';
  } else if (effectiveQuotaExhausted) {
    lockType = 'prime';
  }

  return {
    showLock,
    lockType,
    isLockedPlus,
    unlockedBySellableStore: false
  };
};
